import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

export default function PaymentResult({ status }) {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('checking'); // checking | paid | pending | failed | cancel
  const [attempts, setAttempts] = useState(0);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (status === 'cancel') {
      setState('cancel');
      return;
    }
    if (!sessionId) {
      setState('failed');
      return;
    }
    let cancelled = false;
    const poll = async (tries) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setState('paid');
          refresh();
          return;
        }
        if (data.payment_status === 'failed' || data.payment_status === 'expired') {
          setState('failed');
          return;
        }
        if (tries < 10) {
          setAttempts(tries + 1);
          setTimeout(() => poll(tries + 1), 2000);
        } else {
          setState('pending');
        }
      } catch {
        setState('failed');
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, status]);

  const config = {
    checking: { icon: null, color: 'text-navy', title: 'Verifying payment…', desc: `Please wait (${attempts}/10)` },
    paid: { icon: CheckCircle2, color: 'text-teal', title: 'Payment successful!', desc: 'Your family subscription is now active.' },
    pending: { icon: null, color: 'text-navy', title: 'Still processing…', desc: 'This may take a moment. Refresh your dashboard in a bit.' },
    failed: { icon: XCircle, color: 'text-red-500', title: 'Payment failed', desc: 'Something went wrong. Please try again.' },
    cancel: { icon: XCircle, color: 'text-navy', title: 'Checkout cancelled', desc: 'No worries — you can subscribe anytime.' },
  }[state];

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4">
      <div className="bg-white rounded-3xl p-8 shadow-squishy border-2 border-cream max-w-md text-center" data-testid="payment-result-card">
        {config.icon && <config.icon className={`w-16 h-16 mx-auto mb-4 ${config.color}`} />}
        <h1 className="font-heading text-2xl font-bold text-navy mb-2" data-testid="payment-result-title">{config.title}</h1>
        <p className="text-navy/70 mb-6">{config.desc}</p>
        <Link
          to="/dashboard"
          className="inline-block px-6 py-3 rounded-full bg-navy text-white font-heading font-bold hover:scale-105 active:scale-95 transition-transform duration-200"
          data-testid="payment-result-dashboard-link"
        >
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
}
